import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  EyeIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  FunnelIcon,
  SparklesIcon,
  CursorArrowRaysIcon,
  ClockIcon,
  DocumentChartBarIcon,
  FireIcon,
  LinkIcon,
  HandRaisedIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline';
import { useOpportunityAnalytics } from '../../hooks/useOpportunityAnalytics';
import { ANALYTICS_EVENTS } from '../../services/firebase/opportunity-analytics';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebase/firebase-config';

/**
 * AnalyticsDashboard component for admin analytics view
 * Shows connection success rates, opportunity engagement metrics, and user behavior insights
 */
const AnalyticsDashboard = () => {
  const { getOverallAnalyticsSummary, getConversionMetrics } = useOpportunityAnalytics();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [opportunityPerformance, setOpportunityPerformance] = useState([]);
  const [connectionStats, setConnectionStats] = useState(null);
  const [userBehaviorInsights, setUserBehaviorInsights] = useState(null);
  const [jobPreferenceCounts, setJobPreferenceCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7d'); // '7d', '30d', '90d', 'all'
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'conversions', 'engagement', 'opportunities'

  // Fetch opportunity performance data
  const fetchOpportunityPerformance = async (startDate, endDate) => {
    try {
      // Get all startup opportunities
      const opportunitiesQuery = collection(db, 'startup_opportunities');
      const opportunitiesSnapshot = await getDocs(opportunitiesQuery);
      
      const performanceData = [];
      
      for (const doc of opportunitiesSnapshot.docs) {
        const opportunity = { id: doc.id, ...doc.data() };
        
        // Get analytics for this opportunity
        const analyticsQuery = query(
          collection(db, 'opportunity_analytics'),
          where('opportunityId', '==', doc.id),
          ...(startDate ? [where('timestamp', '>=', startDate)] : []),
          ...(endDate ? [where('timestamp', '<=', endDate)] : []),
          orderBy('timestamp', 'desc')
        );
        
        const analyticsSnapshot = await getDocs(analyticsQuery);
        const events = analyticsSnapshot.docs.map(doc => doc.data());
        
        // Calculate metrics
        const views = events.filter(e => e.eventType === ANALYTICS_EVENTS.OPPORTUNITY_VIEW).length;
        const detailsViews = events.filter(e => e.eventType === ANALYTICS_EVENTS.OPPORTUNITY_DETAILS_VIEW).length;
        const connectionRequests = events.filter(e => e.eventType === ANALYTICS_EVENTS.CONNECTION_REQUEST).length;
        const introductions = events.filter(e => e.eventType === ANALYTICS_EVENTS.CONNECTION_INTRODUCED).length;
        
        const engagementRate = views > 0 ? ((detailsViews / views) * 100).toFixed(1) : '0.0';
        const connectionRate = detailsViews > 0 ? ((connectionRequests / detailsViews) * 100).toFixed(1) : '0.0';
        const successRate = connectionRequests > 0 ? ((introductions / connectionRequests) * 100).toFixed(1) : '0.0';
        
        performanceData.push({
          id: doc.id,
          companyName: opportunity.companyName || 'Unknown Company',
          fundingStage: opportunity.fundingStage || 'Unknown',
          roleArea: opportunity.roleArea || 'Unknown',
          views,
          detailsViews,
          connectionRequests,
          introductions,
          engagementRate: parseFloat(engagementRate),
          connectionRate: parseFloat(connectionRate),
          successRate: parseFloat(successRate),
          createdAt: opportunity.createdAt,
          status: opportunity.status || 'active'
        });
      }
      
      // Sort by total engagement (views + details + connections)
      performanceData.sort((a, b) => {
        const aTotal = a.views + a.detailsViews + a.connectionRequests;
        const bTotal = b.views + b.detailsViews + b.connectionRequests;
        return bTotal - aTotal;
      });
      
      return performanceData;
    } catch (error) {
      console.error('Error fetching opportunity performance:', error);
      return [];
    }
  };

  // Fetch connection statistics
  const fetchConnectionStats = async (startDate, endDate) => {
    try {
      // Get connection requests
      let connectionQuery = collection(db, 'connection_requests');
      
      if (startDate || endDate) {
        const constraints = [];
        if (startDate) constraints.push(where('createdAt', '>=', startDate));
        if (endDate) constraints.push(where('createdAt', '<=', endDate));
        connectionQuery = query(connectionQuery, ...constraints);
      }
      
      const connectionSnapshot = await getDocs(connectionQuery);
      const connections = connectionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Calculate statistics
      const totalRequests = connections.length;
      const pendingRequests = connections.filter(c => c.status === 'pending').length;
      const approvedRequests = connections.filter(c => c.status === 'approved').length;
      const declinedRequests = connections.filter(c => c.status === 'declined').length;
      const introducedRequests = connections.filter(c => c.status === 'introduced').length;
      
      // Calculate average processing time
      const processedConnections = connections.filter(c => c.processedAt && c.createdAt);
      const avgProcessingTime = processedConnections.length > 0 ?
        processedConnections.reduce((total, conn) => {
          const created = conn.createdAt.toDate ? conn.createdAt.toDate() : new Date(conn.createdAt);
          const processed = conn.processedAt.toDate ? conn.processedAt.toDate() : new Date(conn.processedAt);
          return total + (processed - created);
        }, 0) / processedConnections.length : 0;
      
      const avgProcessingHours = (avgProcessingTime / (1000 * 60 * 60)).toFixed(1);
      
      // Get top requesting users
      const userRequestCounts = {};
      connections.forEach(conn => {
        userRequestCounts[conn.userId] = (userRequestCounts[conn.userId] || 0) + 1;
      });
      
      const topUsers = Object.entries(userRequestCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([userId, count]) => ({ userId, requestCount: count }));
      
      return {
        totalRequests,
        pendingRequests,
        approvedRequests,
        declinedRequests,
        introducedRequests,
        approvalRate: totalRequests > 0 ? ((approvedRequests / totalRequests) * 100).toFixed(1) : '0.0',
        successRate: approvedRequests > 0 ? ((introducedRequests / approvedRequests) * 100).toFixed(1) : '0.0',
        avgProcessingHours,
        topUsers
      };
    } catch (error) {
      console.error('Error fetching connection stats:', error);
      return null;
    }
  };

  // Fetch user behavior insights
  const fetchUserBehaviorInsights = async (startDate, endDate) => {
    try {
      // Get analytics events
      let analyticsQuery = collection(db, 'opportunity_analytics');
      
      if (startDate || endDate) {
        const constraints = [];
        if (startDate) constraints.push(where('timestamp', '>=', startDate));
        if (endDate) constraints.push(where('timestamp', '<=', endDate));
        constraints.push(orderBy('timestamp', 'desc'));
        constraints.push(limit(10000)); // Limit to prevent large queries
        analyticsQuery = query(analyticsQuery, ...constraints);
      }
      
      const analyticsSnapshot = await getDocs(analyticsQuery);
      const events = analyticsSnapshot.docs.map(doc => doc.data());
      
      // Calculate user behavior metrics
      const uniqueUsers = [...new Set(events.filter(e => e.userId).map(e => e.userId))];
      const totalSessions = events.length;
      
      // Calculate average session metrics
      const userSessions = {};
      events.forEach(event => {
        if (event.userId) {
          if (!userSessions[event.userId]) {
            userSessions[event.userId] = [];
          }
          userSessions[event.userId].push(event);
        }
      });
      
      const sessionLengths = Object.values(userSessions).map(userEvents => {
        if (userEvents.length < 2) return 0;
        const sortedEvents = userEvents.sort((a, b) => {
          const aTime = a.timestamp.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
          const bTime = b.timestamp.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
          return aTime - bTime;
        });
        const firstEvent = sortedEvents[0].timestamp.toDate ? sortedEvents[0].timestamp.toDate() : new Date(sortedEvents[0].timestamp);
        const lastEvent = sortedEvents[sortedEvents.length - 1].timestamp.toDate ? sortedEvents[sortedEvents.length - 1].timestamp.toDate() : new Date(sortedEvents[sortedEvents.length - 1].timestamp);
        return lastEvent - firstEvent;
      });
      
      const avgSessionLength = sessionLengths.length > 0 ?
        (sessionLengths.reduce((total, length) => total + length, 0) / sessionLengths.length / (1000 * 60)).toFixed(1) :
        '0.0';
      
      // Calculate bounce rate (users who only viewed one opportunity)
      const singleViewUsers = Object.values(userSessions).filter(userEvents => 
        userEvents.filter(e => e.eventType === ANALYTICS_EVENTS.OPPORTUNITY_VIEW).length === 1
      ).length;
      
      const bounceRate = uniqueUsers.length > 0 ? ((singleViewUsers / uniqueUsers.length) * 100).toFixed(1) : '0.0';
      
      // Most popular search terms
      const searchEvents = events.filter(e => e.eventType === ANALYTICS_EVENTS.OPPORTUNITY_SEARCH);
      const searchTerms = {};
      searchEvents.forEach(event => {
        if (event.searchData?.searchTerm) {
          const term = event.searchData.searchTerm.toLowerCase();
          searchTerms[term] = (searchTerms[term] || 0) + 1;
        }
      });
      
      const topSearchTerms = Object.entries(searchTerms)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([term, count]) => ({ term, count }));
      
      // Most used filters
      const filterEvents = events.filter(e => e.eventType === ANALYTICS_EVENTS.OPPORTUNITY_FILTER);
      const filterUsage = {};
      filterEvents.forEach(event => {
        if (event.filterData?.appliedFilters) {
          Object.keys(event.filterData.appliedFilters).forEach(filterType => {
            filterUsage[filterType] = (filterUsage[filterType] || 0) + 1;
          });
        }
      });
      
      const topFilters = Object.entries(filterUsage)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([filter, count]) => ({ filter, count }));
      
      return {
        uniqueUsers: uniqueUsers.length,
        totalSessions,
        avgSessionLength,
        bounceRate,
        topSearchTerms,
        topFilters,
        avgEventsPerUser: uniqueUsers.length > 0 ? (totalSessions / uniqueUsers.length).toFixed(1) : '0.0'
      };
    } catch (error) {
      console.error('Error fetching user behavior insights:', error);
      return null;
    }
  };

  // Fetch job preference counts
  const fetchJobPreferenceCounts = async () => {
    try {
      // Get all user profiles
      const userProfilesQuery = collection(db, 'user_profiles');
      const userProfilesSnapshot = await getDocs(userProfilesQuery);
      
      // Count job search statuses
      const counts = {
        'actively-looking': 0,
        'open-to-opportunities': 0,
        'not-interested': 0,
        'not-specified': 0,
        total: 0
      };
      
      userProfilesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const jobSearchStatus = data.preferences?.jobSearchStatus;
        
        counts.total++;
        
        if (jobSearchStatus === 'actively-looking') {
          counts['actively-looking']++;
        } else if (jobSearchStatus === 'open-to-opportunities') {
          counts['open-to-opportunities']++;
        } else if (jobSearchStatus === 'not-interested') {
          counts['not-interested']++;
        } else {
          counts['not-specified']++;
        }
      });
      
      return counts;
    } catch (error) {
      console.error('Error fetching job preference counts:', error);
      return null;
    }
  };

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Calculate date range
        let startDate = null;
        const now = new Date();
        
        if (dateRange !== 'all') {
          const days = parseInt(dateRange.replace('d', ''));
          startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        }
        
        // Fetch all analytics data in parallel
        const [
          overallResult,
          conversionResult,
          opportunityPerformanceData,
          connectionStatsData,
          userBehaviorData,
          jobPreferenceData
        ] = await Promise.all([
          getOverallAnalyticsSummary({
            startDate,
            endDate: now,
            limit: 5000
          }),
          getConversionMetrics({
            startDate,
            endDate: now
          }),
          fetchOpportunityPerformance(startDate, now),
          fetchConnectionStats(startDate, now),
          fetchUserBehaviorInsights(startDate, now),
          fetchJobPreferenceCounts()
        ]);
        
        if (overallResult.success) {
          setAnalyticsData(overallResult.data);
        } else {
          setError(overallResult.error || 'Failed to fetch analytics data');
        }
        
        if (conversionResult.success) {
          setConversionData(conversionResult.data);
        }
        
        setOpportunityPerformance(opportunityPerformanceData);
        setConnectionStats(connectionStatsData);
        setUserBehaviorInsights(userBehaviorData);
        setJobPreferenceCounts(jobPreferenceData);
        
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(err.message || 'Failed to fetch analytics data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [dateRange, getOverallAnalyticsSummary, getConversionMetrics]);

  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  // Calculate additional metrics
  const getAdditionalMetrics = () => {
    if (!analyticsData) return {};
    
    const { summary } = analyticsData;
    const eventBreakdown = summary.eventBreakdown || {};
    
    // Calculate engagement rate (details views / total views)
    const totalViews = eventBreakdown[ANALYTICS_EVENTS.OPPORTUNITY_VIEW] || 0;
    const detailsViews = eventBreakdown[ANALYTICS_EVENTS.OPPORTUNITY_DETAILS_VIEW] || 0;
    const connectionRequests = eventBreakdown[ANALYTICS_EVENTS.CONNECTION_REQUEST] || 0;
    const introductions = eventBreakdown[ANALYTICS_EVENTS.CONNECTION_INTRODUCED] || 0;
    
    const engagementRate = totalViews > 0 ? ((detailsViews / totalViews) * 100).toFixed(1) : '0.0';
    const connectionRate = detailsViews > 0 ? ((connectionRequests / detailsViews) * 100).toFixed(1) : '0.0';
    const successRate = connectionRequests > 0 ? ((introductions / connectionRequests) * 100).toFixed(1) : '0.0';
    
    return {
      engagementRate,
      connectionRate,
      successRate,
      totalViews,
      detailsViews,
      connectionRequests,
      introductions
    };
  };

  const additionalMetrics = getAdditionalMetrics();

  // Get top opportunities by engagement
  const getTopOpportunities = () => {
    if (!analyticsData) return [];
    
    const { summary } = analyticsData;
    const topOpportunities = summary.topOpportunities || {};
    
    return Object.entries(topOpportunities)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([opportunityId, count]) => ({
        id: opportunityId,
        engagementCount: count
      }));
  };

  const topOpportunities = getTopOpportunities();

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'conversions':
        return renderConversionsTab();
      case 'engagement':
        return renderEngagementTab();
      case 'opportunities':
        return renderOpportunitiesTab();
      default:
        return renderOverviewTab();
    }
  };

  // Overview Tab Component
  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Events */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Events</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {analyticsData?.summary?.totalEvents?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Unique Users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Users</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {analyticsData?.summary?.uniqueUsers?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <UserGroupIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        {/* Engagement Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Engagement Rate</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {additionalMetrics.engagementRate}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Details views / Total views
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <EyeIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        {/* Connection Success Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {additionalMetrics.successRate}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Introductions / Requests
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
              <SparklesIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Job Preference Counts */}
      {jobPreferenceCounts && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Job Search Preferences</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Actively Looking */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">Actively Looking</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {jobPreferenceCounts['actively-looking']}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {jobPreferenceCounts.total > 0 ? 
                      Math.round((jobPreferenceCounts['actively-looking'] / jobPreferenceCounts.total) * 100) : 0}%
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>

            {/* Open to Opportunities */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Considering</p>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                    {jobPreferenceCounts['open-to-opportunities']}
                  </p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    {jobPreferenceCounts.total > 0 ? 
                      Math.round((jobPreferenceCounts['open-to-opportunities'] / jobPreferenceCounts.total) * 100) : 0}%
                  </p>
                </div>
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                  <PauseIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </div>

            {/* Not Interested */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">Not Looking</p>
                  <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                    {jobPreferenceCounts['not-interested']}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {jobPreferenceCounts.total > 0 ? 
                      Math.round((jobPreferenceCounts['not-interested'] / jobPreferenceCounts.total) * 100) : 0}%
                  </p>
                </div>
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <NoSymbolIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>

            {/* Not Specified */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Not Specified</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {jobPreferenceCounts['not-specified']}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {jobPreferenceCounts.total > 0 ? 
                      Math.round((jobPreferenceCounts['not-specified'] / jobPreferenceCounts.total) * 100) : 0}%
                  </p>
                </div>
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                  <UserGroupIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total Users: {jobPreferenceCounts.total}
            </p>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Quick Summary</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
              {analyticsData?.summary?.uniqueOpportunities || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Active Opportunities</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
              {analyticsData?.summary?.conversionMetrics?.viewToDetailsRate || '0.0'}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">View to Details Rate</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
              {analyticsData?.summary?.conversionMetrics?.connectionToIntroductionRate || '0.0'}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Connection to Introduction Rate</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Conversions Tab Component
  const renderConversionsTab = () => (
    <div className="space-y-6">
      {/* Conversion Funnel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          Conversion Funnel
        </h3>
        
        <div className="space-y-4">
          {/* Opportunity Views */}
          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="font-medium text-gray-900 dark:text-white">Opportunity Views</span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {additionalMetrics.totalViews?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">100%</div>
            </div>
          </div>

          {/* Details Views */}
          <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span className="font-medium text-gray-900 dark:text-white">Details Views</span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {additionalMetrics.detailsViews?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {additionalMetrics.engagementRate}%
              </div>
            </div>
          </div>

          {/* Connection Requests */}
          <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="font-medium text-gray-900 dark:text-white">Connection Requests</span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {additionalMetrics.connectionRequests?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {additionalMetrics.connectionRate}%
              </div>
            </div>
          </div>

          {/* Successful Introductions */}
          <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              <span className="font-medium text-gray-900 dark:text-white">Successful Introductions</span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {additionalMetrics.introductions?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {additionalMetrics.successRate}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connection Statistics */}
      {connectionStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <HandRaisedIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              Connection Request Status
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  Pending
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {connectionStats.pendingRequests}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <CheckCircleIcon className="w-3 h-3 text-green-500" />
                  Approved
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {connectionStats.approvedRequests}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <XCircleIcon className="w-3 h-3 text-red-500" />
                  Declined
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {connectionStats.declinedRequests}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <LinkIcon className="w-3 h-3 text-blue-500" />
                  Introduced
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {connectionStats.introducedRequests}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Processing Metrics</h4>
            
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                  {connectionStats.approvalRate}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Approval Rate</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {connectionStats.avgProcessingHours}h
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Avg Processing Time</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Engagement Tab Component
  const renderEngagementTab = () => (
    <div className="space-y-6">
      {userBehaviorInsights && (
        <>
          {/* User Behavior Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg. Session Length</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {userBehaviorInsights.avgSessionLength}m
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bounce Rate</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {userBehaviorInsights.bounceRate}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <ArrowTrendingDownIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg. Events per User</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {userBehaviorInsights.avgEventsPerUser}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <CursorArrowRaysIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Top Search Terms</h4>
              
              {userBehaviorInsights.topSearchTerms.length > 0 ? (
                <div className="space-y-3">
                  {userBehaviorInsights.topSearchTerms.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {item.term}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {item.count} searches
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No search data available</p>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Most Used Filters</h4>
              
              {userBehaviorInsights.topFilters.length > 0 ? (
                <div className="space-y-3">
                  {userBehaviorInsights.topFilters.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {item.filter.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {item.count} uses
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No filter data available</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Opportunities Tab Component
  const renderOpportunitiesTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <FireIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          Opportunity Performance
        </h3>
        
        {opportunityPerformance.length > 0 ? (
          <div className="table-container-scroll">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Introductions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {opportunityPerformance.slice(0, 10).map((opportunity) => (
                  <tr key={opportunity.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {opportunity.companyName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {opportunity.roleArea}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {opportunity.fundingStage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {opportunity.views}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {opportunity.detailsViews}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {opportunity.connectionRequests}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {opportunity.introductions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {opportunity.successRate}%
                        </span>
                        {opportunity.successRate > 50 && (
                          <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 ml-1" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <DocumentChartBarIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No opportunity performance data available</p>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          ))}
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h2>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 text-red-600 dark:text-red-400">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200">Error Loading Analytics</h3>
              <p className="text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h2>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-12 text-center">
          <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Analytics Data</h3>
          <p className="text-gray-600 dark:text-gray-400">No analytics data available for the selected time period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Date Range Selector and Tabs */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h2>
        
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-gray-500" />
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Dashboard Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4 scrollbar-styled pb-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'overview'
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-b-2 border-purple-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-4 h-4" />
              <span>Overview</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('conversions')}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'conversions'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-b-2 border-green-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-4 h-4" />
              <span>Conversions</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('engagement')}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'engagement'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <CursorArrowRaysIcon className="w-4 h-4" />
              <span>User Engagement</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('opportunities')}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'opportunities'
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-b-2 border-amber-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <DocumentChartBarIcon className="w-4 h-4" />
              <span>Opportunity Performance</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default AnalyticsDashboard;